import { useCustomQuery } from "@/hooks/useCustomQuery"
import { Box, Typography, Chip, Grid } from "@mui/material"
import { useParams, Link } from "react-router-dom"
import StatusBadge from "@/components/ui/badge"

const CategoryDetails = () => {
  const { id } = useParams()

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useCustomQuery(["category-detail", id], `category/${id}`)

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error loading category: {error?.message}</div>
  }

  const category = response?.data

  if (!category) {
    return <div>Category not found</div>
  }

  return (
    <div>
      <div className="breadcrumb-card">
        <h5>Category Details</h5>

        <ul className="breadcrumb">
          <li>
            <Link to="/pos/dashboard">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>
            <Link to="/pos/categories">Categories</Link>
          </li>
          <li>{category.name}</li>
        </ul>
      </div>

      {category ? (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-4 xxl:tw-grid-cols-5 tw-gap-6">
          <div className="card bg-white border-0 px-4 rounded-3 mb-4 lg:col-span-3 xxl:col-span-4">
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Category Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {category.name}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Status
                  </Typography>
                  <StatusBadge name={category.status} />
                </Grid>

                {category.description && (
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Description
                    </Typography>
                    <Typography variant="body1">
                      {category.description}
                    </Typography>
                  </Grid>
                )}

                {category.image && (
                  <Grid item xs={12}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Image
                    </Typography>
                    <Box
                      component="img"
                      src={category.image}
                      alt={category.name}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 300,
                        borderRadius: 2,
                        objectFit: "contain",
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Tags
                  </Typography>
                  {category.tags && category.tags.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {category.tags.map(
                        (tag: { id: number; name: string }) => (
                          <Chip
                            key={tag.id}
                            label={tag.name}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                        )
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tags assigned
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    Created At
                  </Typography>
                  <Typography variant="body2">{category.created_at}</Typography>
                </Grid>

                {category.updated_at && (
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Updated At
                    </Typography>
                    <Typography variant="body2">
                      {category.updated_at}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </div>
        </div>
      ) : (
        <div>No category data found.</div>
      )}
    </div>
  )
}

export default CategoryDetails
